///////////////////
// Favorite Store
//////////////////
class FavoriteStore {
  constructor() {
    this.latestFavoriteToken = 0;
    this.init();
  }

  init() {
    // Add document-level click listener for all fav-location elements
    document.addEventListener('click', (event) => {
      const favIcon = event.target.closest('.fav-location');
      if (!favIcon) return;

      const favoriteContainer = favIcon.closest('[data-favorite-store]');
      if (!favoriteContainer) return;

      const storeHandle = favoriteContainer.getAttribute('data-favorite-store');
      const isCurrentlyFav = localStorage.getItem('tg--webcheckin-fav') === storeHandle;

      // If the clicked store is already the favorite, remove it. Otherwise, set as favorite.
      document.dispatchEvent(new CustomEvent('favorite-store:change', {
        detail: {
          storeHandle,
          isFavorite: !isCurrentlyFav && !!storeHandle // false if already favorite, true if not
        }
      }));
    });

    // Listen for favorite store changes
    document.addEventListener('favorite-store:change', this.handleFavoriteChange.bind(this));

    // Sync with page barbershop ID on load
    this.syncWithPageBarbershop();
  }

  async handleFavoriteChange(event) {
    // Generate a new token for this favorite action
    const myToken = ++this.latestFavoriteToken;

    const { storeHandle, isFavorite } = event.detail;
    const prevFav = localStorage.getItem('tg--webcheckin-fav');

    // Optimistically update UI
    this.updateFavoriteUI(storeHandle, isFavorite);

    // Try to update localStorage and cart
    const success = await this.updateFavoriteStore(isFavorite ? storeHandle : null, myToken);

    // If this is not the latest favorite action, abort
    if (myToken !== this.latestFavoriteToken) return;

    // If update failed, revert UI to previous state
    if (!success) {
      this.updateFavoriteUI(prevFav, !!prevFav);
    }
  }

  async updateFavoriteStore(storeHandle, token) {
    // Update localStorage
    if (storeHandle) {
      localStorage.setItem('tg--webcheckin-fav', storeHandle);
    } else {
      localStorage.removeItem('tg--webcheckin-fav');
    }

    // Update cart attribute; the re-rendered sections come back in the same response
    const selectors = [
      { sectionId: 'header', selector: '.header-location' },
      { sectionId: 'header', selector: '#barbershop-drawer' }
    ];
    const result = await this.updateCartAttribute(storeHandle, selectors);

    if (result.success) {
      this.applyRenderedSections(result.sections, selectors, token ?? this.latestFavoriteToken);
    }

    // Log current state
    // console.log('Favorite Store Updated:', storeHandle || 'none');

    return result.success;
  }

  updateFavoriteUI(storeHandle, isFavorite) {
    // Remove favorite class from all elements
    document.querySelectorAll('.is-fav').forEach(elem => elem.classList.remove('is-fav'));

    // Add favorite class if needed
    if (isFavorite) {
      document.querySelectorAll(`[data-favorite-store="${storeHandle}"]`).forEach(e => {
        e.classList.add('is-fav');
        // Animate the card to the top of its list
        // For main locator, animate li.location
        if (e.closest('.locations-nearest__list')) {
          const card = e.closest('li.location');
          if (card && card.parentElement.firstChild !== card) {
            flipMoveToTopGeneric(card, '.locations-nearest__list');
          }
        }
        // For city-lp, animate .city-wrap
        else if (e.closest('.locations-lp')) {
          const cityWrap = e.closest('.city-wrap');
          if (cityWrap && cityWrap.parentElement.firstChild !== cityWrap) {
            flipMoveToTopGeneric(cityWrap, '.locations-lp');
          }
        }
        // Add more selectors as needed for other contexts
      });
    }
  }

  async updateCartAttribute(storeHandle, selectors) {
    try {
      const sectionIds = selectors ? [...new Set(selectors.map(s => s.sectionId))] : null;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // keepalive lets the request complete even if the page is refreshed mid-flight
        keepalive: true,
        body: JSON.stringify({
          attributes: {
            'Favorite Store': storeHandle
          },
          // Bundled section rendering: Shopify returns the re-rendered sections in
          // this same response, saving the extra section-render round trips
          ...(sectionIds && {
            sections: sectionIds,
            sections_url: window.location.pathname
          })
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const data = await response.json();
      return { success: true, sections: data.sections || {} };
    } catch (error) {
      console.error('Error updating cart attribute:', error);
      return { success: false, sections: {} };
    }
  }

  // Swap in the section HTML that came back with the cart update
  applyRenderedSections(sectionsHtml, selectors, token = this.latestFavoriteToken) {
    if (!sectionsHtml || !selectors.length) return;
    // If a new favorite action has started, abort this render
    if (token !== this.latestFavoriteToken) return;

    const parser = new DOMParser();
    const docs = {};

    for (const { sectionId, selector } of selectors) {
      // If a new favorite action has started, abort this render
      if (token !== this.latestFavoriteToken) return;
      if (!sectionsHtml[sectionId]) continue;

      docs[sectionId] = docs[sectionId] || parser.parseFromString(sectionsHtml[sectionId], 'text/html');
      const newElement = docs[sectionId].querySelector(selector);
      const currentElement = document.querySelector(selector);

      if (newElement && currentElement) {
        currentElement.replaceWith(newElement);

        // Replace all barbershop buttons with new HTML
        if (selector === '.header-location') {
          // Start the fresh content in the placeholder state so removing the
          // class below fades the new text in. (The Liquid only adds
          // loading-location when request.path is '/', which no longer holds
          // now that sections render against the current page URL.)
          newElement.querySelectorAll('.content').forEach(content => {
            content.classList.add('loading-location');
          });

          // Find all existing barbershop buttons and replace them
          const allButtons = document.querySelectorAll('.barbershop-button');
          allButtons.forEach(button => {
            button.outerHTML = newElement.outerHTML;
          });

          // Remove loading class from all updated buttons after replacement
          requestAnimationFrame(() => {
            document.querySelectorAll('.barbershop-button .content').forEach(content => {
              content.classList.remove('loading-location');
            });
          });
        }

        // Dispatch event if this is the barbershop drawer
        if (selector === '#barbershop-drawer') {
          document.dispatchEvent(new CustomEvent('barbershop-drawer:after:rendered'));
        }

        // The locations list renders fresh cards: re-apply fav hearts and let
        // other scripts (wait times) re-initialize against the new elements
        if (selector === '.locations-wrapper') {
          const favStore = localStorage.getItem('tg--webcheckin-fav');
          if (favStore && favStore !== 'undefined') {
            this.updateFavoriteUI(favStore, true);
          }
          document.dispatchEvent(new CustomEvent('locations-list:after:rendered'));
        }
      }
    }

    // Injected section HTML doesn't execute its inline scripts, so fill any
    // fresh [data-shop-hours] status elements manually
    if (window.tgShopTime) window.tgShopTime.fill();
  }

  // Header sections plus, when present, the map-list locations list —
  // it's server-sorted (favorite first), so it must re-render on cart changes
  getSyncRenderJobs() {
    const jobs = [
      { sectionId: 'header', selector: '.header-location' },
      { sectionId: 'header', selector: '#barbershop-drawer' }
    ];
    const list = document.querySelector('.locations-wrapper');
    const sectionWrapper = list && list.closest('[id^="shopify-section-"]');
    if (sectionWrapper) {
      jobs.push({
        sectionId: sectionWrapper.id.replace('shopify-section-', ''),
        selector: '.locations-wrapper'
      });
    }
    return jobs;
  }

  async syncWithPageBarbershop() {
    const stored = localStorage.getItem('tg--webcheckin-fav');
    const favStore = !stored || stored === 'undefined' ? null : stored;
    const pageFavoriteStore = document.documentElement.getAttribute('data-barbershop-id') || null;

    // Page HTML agrees with localStorage — just reflect the fav state on cards
    if (favStore === pageFavoriteStore) {
      if (favStore) this.updateFavoriteUI(favStore, true);
      return;
    }

    // The page disagrees with localStorage, but it may just be a stale Shopify
    // page-cache copy. Ask the real cart (never page-cached) before correcting.
    const cartFavStore = await this.fetchCartFavoriteStore();

    if (cartFavStore !== undefined && cartFavStore === favStore) {
      // Cart is already correct — this page is stale cache. Leave the
      // server-rendered header alone; it settles once the cache expires.
      if (favStore) this.updateFavoriteUI(favStore, true);
      return;
    }

    // Genuine mismatch — update the cart and re-render from its response
    if (favStore) this.updateFavoriteUI(favStore, true);
    const jobs = this.getSyncRenderJobs();
    const result = await this.updateCartAttribute(favStore, jobs);
    if (result.success) {
      this.applyRenderedSections(result.sections, jobs);
    }
  }

  // Reads the favorite store attribute from the live cart, bypassing the page
  // cache. Returns the handle, null when unset, or undefined if the check failed.
  async fetchCartFavoriteStore() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return undefined;
      const cart = await response.json();
      const value = cart.attributes && cart.attributes['Favorite Store'];
      return value ? value : null;
    } catch (error) {
      return undefined;
    }
  }
}

///////////////////
// Nav Drawer
//////////////////
async function navDrawer() {
  // const buttons = document.querySelectorAll('[data-nav-drawer]');
  const buttons = document.querySelectorAll('.has-drawer-nav summary');
  const navDrawer = document.getElementById('nav-drawer');
  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      if (theme.mediaMatches.md) {
        navDrawer.open(button);
      }
    });
  });

  // Appends drawer nav to body (once) before open, to avoid z-index issues with overlay and header.
  if (navDrawer.parentElement !== document.body) {
    document.body.appendChild(navDrawer);
  }

  // Somewhere in your theme initialization
  theme.navDrawerIsOpen = false;

  navDrawer.addEventListener('on:nav-drawer:before-open', (event) => {
    // console.log('Drawer opening!', event);
    theme.navDrawerIsOpen = true;
  });
  navDrawer.addEventListener('on:nav-drawer:after-open', (event) => {
    // console.log('Drawer is open!', event);
  });
  navDrawer.addEventListener('on:nav-drawer:after-close', (event) => {
    // console.log('Drawer closed!', event);
    theme.navDrawerIsOpen = false;
  });

  // Attach a single event listener to the nav-drawer container
  navDrawer.addEventListener('click', (event) => {
    // Scroll all ul elements inside navDrawer to top
    navDrawer.querySelectorAll('ul').forEach(ul => {
      ul.scrollTo({ top: 0, behavior: 'smooth' });
    });

    const target = event.target;

    if (target.matches('.nav-back')) {
      handleBackClick(target);
    } else if (target.matches('.nested-nav')) {
      handleNavItemClick(target);
    }
  });
}

function handleNavItemClick(navItem) {
  const li = navItem.closest('li');
  if (!li) return;
  // Using :scope > ul ensures we only get the direct child menu
  const childMenu = li.querySelector(':scope > ul');
  // const parentUl = navItem.closest('ul');
  // parentUl.scrollTop = 0;
  if (childMenu && childMenu.getAttribute('data-visible') === 'false') {
    childMenu.setAttribute('data-visible', 'true');
    childMenu.setAttribute('open', '');
  }
}

function handleBackClick(backButton) {
  // The parent of the back button is the menu <ul> that we want to close.
  const parentMenu = backButton.parentElement;
  if (parentMenu && parentMenu.hasAttribute('data-visible')) {
    // Close any open child menus within the parent menu.
    const openSubMenus = parentMenu.querySelectorAll('ul[open]');
    openSubMenus.forEach(submenu => {
      submenu.setAttribute('data-visible', 'false');
      submenu.removeAttribute('open');
    });
    // Now close the parent menu itself.
    parentMenu.setAttribute('data-visible', 'false');
    parentMenu.removeAttribute('open');
  }
}

/**
 * Animate moving a card to the top of a list using FLIP.
 * @param {Element} card - The card element to move (optional if newOrderElements is provided).
 * @param {string} containerSelector - Selector for the parent container.
 * @param {boolean} stagger - Whether to stagger the animation (default: false).
 * @param {boolean} moveToTop - Whether to move card to top (default: true). Ignored if newOrderElements is present.
 * @param {Array<Element>} newOrderElements - Array of elements in their new order (optional).
 */
function flipMoveToTopGeneric(card, containerSelector, stagger = false, moveToTop = true, newOrderElements = null) {
  // If we have new elements, getting parent from the first one is safer than card which might be null
  const parent = newOrderElements && newOrderElements.length > 0
    ? newOrderElements[0].parentElement
    : (card ? card.closest(containerSelector) : document.querySelector(containerSelector));

  if (!parent) return;

  const cards = Array.from(parent.children);

  // 1. Record the first positions of all cards
  const firstRects = cards.map(c => c.getBoundingClientRect());

  // 2. Perform DOM updates
  if (newOrderElements) {
    // Reappend all elements in new order
    newOrderElements.forEach(el => parent.appendChild(el));
  } else if (moveToTop && card) {
    parent.insertBefore(card, parent.firstChild);
  }

  // 3. Record the last positions of all cards
  const lastRects = Array.from(parent.children).map(c => c.getBoundingClientRect());

  // 4. For each card, apply the FLIP transform
  const movingCards = new Set();
  const currentChildren = Array.from(parent.children);

  // Note: cards array is still the original order, which matches firstRects indices
  cards.forEach((c, i) => {
    // Find where this card ended up
    const lastIndex = currentChildren.indexOf(c);
    const dy = firstRects[i].top - lastRects[lastIndex].top;

    // Only animate if the movement is significant (> 1px) and UPWARDS (positive dy)
    // Positive dy means start (firstRect) > end (lastRect), so it moved UP
    if (dy > 1) {
      movingCards.add(c);
      c.style.transition = 'none';
      c.style.transform = `translateY(${dy}px)`;
    } else {
      c.style.transform = '';
    }
  });

  // Force reflow
  parent.offsetHeight;

  // 5. Animate all cards to their new positions
  // We define the animation on the elements themselves
  Array.from(parent.children).forEach((c, i) => {
    // Skip if not moving
    if (!movingCards.has(c)) {
      c.style.transform = '';
      return;
    }

    // If we reordered, the index i is the NEW position (0, 1, 2...)
    // Stagger based on visual position
    const delay = stagger ? i * 30 : 0;

    if (delay > 0) {
      c.style.transitionDelay = `${delay}ms`;
    }
    // Add animating class for styling (e.g. z-index)
    c.classList.add('is-animating');

    c.style.transition = 'transform 0.4s cubic-bezier(.4,0,.2,1)';
    c.style.transform = '';

    const handler = (e) => {
      if (e.propertyName === 'transform') {
        c.classList.remove('is-animating');
        c.style.transition = '';
        c.style.transitionDelay = '';
        c.style.transform = '';
        c.removeEventListener('transitionend', handler);
      }
    };
    c.addEventListener('transitionend', handler);
  });

  // Scroll logic
  if (moveToTop && !newOrderElements) { // Only scroll if it's a "move to top" action
    const target = card || parent;
    const scrollContainer = target.closest('.locations.scrollbar');
    if (scrollContainer && scrollContainer.scrollTo) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (window.innerWidth < 991 || !scrollContainer) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}

document.addEventListener("DOMContentLoaded", function () {
  navDrawer();

  // Initialize favorite store functionality
  window.favoriteStore = new FavoriteStore();

  // Listen for FLIP animation requests
  document.addEventListener('dispatch:flip:reorder-cards', (event) => {
    const { containerSelector, stagger, newOrderElements } = event.detail;
    if (containerSelector || newOrderElements) {
      // If we have elements, we can infer selector from parent, so allow null selector
      flipMoveToTopGeneric(null, containerSelector, stagger, false, newOrderElements);
    }
  });
});